/* Oak & Hour theme core. The mockup's machinery rebuilt on Shopify's
   real cart: AJAX Cart API, honest offer estimates that match the
   automatic discounts configured in admin, and the same warm chrome. */

window.OH = window.OH || {};

(function () {
  const C = () => OH.CONFIG;

  /* ---------- tiny utilities ---------- */
  OH.qs = (s, el) => (el || document).querySelector(s);
  OH.qsa = (s, el) => Array.from((el || document).querySelectorAll(s));
  OH.money = cents => {
    const value = Math.round(Number(cents || 0)) / 100;
    return "$" + value.toLocaleString("en-US", {
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
      maximumFractionDigits: 2
    });
  };
  OH.esc = s => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  /* A neutral measurement bridge. It sends nothing by itself. Shopify pixels or a
     consent-aware tag manager can listen for oh:analytics or read dataLayer. */
  OH.track = (event, detail) => {
    const payload = { event, ...(detail || {}) };
    document.dispatchEvent(new CustomEvent("oh:analytics", { detail: payload }));
    if (Array.isArray(window.dataLayer)) window.dataLayer.push(payload);
  };

  OH.hourLine = () => {
    const h = new Date().getHours();
    const t = new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }).toLowerCase();
    return { t, line: h < 11 ? "The morning is long." : h < 17 ? "The afternoon is open." : "The evening is young." };
  };
  OH.store = {
    get(key, fallback) {
      try { const raw = localStorage.getItem("oh:" + key); return raw === null ? fallback : JSON.parse(raw); }
      catch (e) { return fallback; }
    },
    set(key, value) { try { localStorage.setItem("oh:" + key, JSON.stringify(value)); } catch (e) {} }
  };

  /* skipped view transitions and interrupted plays reject with AbortError, benign */
  window.addEventListener("unhandledrejection", e => {
    if (e.reason && e.reason.name === "AbortError") e.preventDefault();
  });

  /* ---------- toast, with an optional action ---------- */
  let toastTimer;
  OH.toast = function (msg, actionLabel, onAction) {
    let el = OH.qs(".toast");
    if (!el) {
      el = document.createElement("div");
      el.className = "toast";
      el.setAttribute("role", "status");
      document.body.appendChild(el);
    }
    el.innerHTML = OH.esc(msg) + (actionLabel ? ` <button class="toast-act">${OH.esc(actionLabel)}</button>` : "");
    el.style.pointerEvents = actionLabel ? "auto" : "none";
    if (actionLabel && onAction) el.querySelector(".toast-act").addEventListener("click", () => { onAction(); el.classList.remove("show"); });
    el.classList.add("show");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove("show"), actionLabel ? 5200 : 2600);
  };

  /* ---------- the real cart ---------- */
  let cartCache = null;
  const addingVariants = new Set();
  const changingLines = new Set();

  async function fetchCart() {
    const res = await fetch("/cart.js");
    cartCache = await res.json();
    return cartCache;
  }

  const giftOptions = () => (C().gifts || []).filter(g => g && g.variantId);
  const giftById = id => giftOptions().find(g => String(g.variantId) === String(id)) || null;
  const isGiftLine = item => String(item && item.properties && item.properties._gift) === "true";
  const chosenGift = () => {
    const stored = OH.store.get("gift-choice", null);
    const id = stored && typeof stored === "object" ? stored.variantId : stored;
    const gift = giftById(id);
    return gift && gift.available !== false ? gift : null;
  };
  OH.giftChoice = chosenGift;

  OH.cart = {
    state: () => cartCache,
    count: () => cartCache ? cartCache.item_count : (C().cartCount || 0),

    async add(variantId, quantity, flyFrom) {
      const key = String(variantId);
      if (addingVariants.has(key)) return false;
      addingVariants.add(key);
      document.dispatchEvent(new CustomEvent("oh:cartbusy", { detail: { busy: true, variantId: key } }));
      try {
        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: variantId, quantity: quantity || 1 })
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          OH.toast(err.description || "We could not add that piece. Please try again.");
          return false;
        }
        const line = await res.json();
        await fetchCart();
        await syncGift();
        if (flyFrom) OH.flyToBag(flyFrom);
        OH.toast((line.product_title || "Added") + " is in your bag");
        OH.track("add_to_cart", {
          ecommerce: {
            currency: line.currency || "USD",
            value: ((line.final_price || line.price || 0) * (line.quantity || quantity || 1)) / 100,
            items: [{ item_id: String(line.variant_id || variantId), item_name: line.product_title || "", item_variant: line.variant_title || "", price: (line.final_price || line.price || 0) / 100, quantity: line.quantity || quantity || 1 }]
          }
        });
        setTimeout(OH.drawer.open, flyFrom && !OH.motion.reduced ? 420 : 0);
        document.dispatchEvent(new CustomEvent("oh:cart"));
        return true;
      } catch (e) {
        OH.toast("We could not reach the bag. Check your connection and try again.");
        return false;
      } finally {
        addingVariants.delete(key);
        document.dispatchEvent(new CustomEvent("oh:cartbusy", { detail: { busy: false, variantId: key } }));
      }
    },

    async change(lineKey, quantity) {
      if (changingLines.has(lineKey)) return false;
      changingLines.add(lineKey);
      const before = cartCache ? cartCache.items.find(i => i.key === lineKey) : null;
      try {
        const res = await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: lineKey, quantity })
        });
        if (!res.ok) throw new Error("cart change failed");
        const cart = await res.json();
        /* Shopify clamps quantity to available stock; say so honestly */
        const after = cart.items ? cart.items.find(i => i.key === lineKey) : null;
        if (quantity > 0 && after && after.quantity < quantity) OH.toast("Only " + after.quantity + " available in that combination");
        cartCache = cart;
        await syncGift();
        if (quantity === 0 && before) {
          OH.toast(before.product_title + " removed.", "Undo", () => OH.cart.add(before.variant_id, before.quantity));
        }
        document.dispatchEvent(new CustomEvent("oh:cart"));
        return true;
      } catch (e) {
        OH.toast("We could not update the bag. Please try again.");
        return false;
      } finally { changingLines.delete(lineKey); }
    },

    /* the honest ladder, estimated exactly as admin's automatic discount computes it */
    totals() {
      const cart = cartCache || { items: [], item_count: 0, total_price: 0 };
      const count = cart.item_count;
      const subtotal = cart.total_price; /* cents */
      /* promotional lines never count toward the threshold they unlock */
      const giftLines = cart.items.filter(isGiftLine);
      const giftLineTotal = giftLines.reduce((sum, line) => sum + line.line_price, 0);
      const choice = chosenGift();
      const giftLine = giftLines.find(line => choice && String(line.variant_id) === String(choice.variantId)) || giftLines[0] || null;
      const qualifying = subtotal - giftLineTotal;
      const dollars = qualifying / 100;
      const tiers = (C().tiers || []).filter(x => x && x.spend > 0).sort((a, b) => a.spend - b.spend);
      const earnedTier = tiers.filter(x => dollars >= x.spend).pop() || null;
      const tierSavings = earnedTier ? earnedTier.off * 100 : 0;
      const welcome = OH.store.get("welcome", {});
      const welcomeSavings = (welcome.applied && dollars >= C().welcomeMin) ? C().welcomeOff * 100 : 0;
      let discountLabel = null, discount = 0;
      if (tierSavings >= welcomeSavings && tierSavings > 0) {
        discountLabel = "Room savings, $" + earnedTier.off + " off $" + earnedTier.spend.toLocaleString("en-US");
        discount = tierSavings;
      } else if (welcomeSavings > 0) {
        discountLabel = "Welcome code " + C().welcomeCode;
        discount = welcomeSavings;
      }
      const merch = subtotal - discount;
      const freeDelivery = merch >= C().freeDeliveryThreshold * 100;
      const giftEarned = qualifying >= C().giftThreshold * 100;
      return { cart, count, subtotal, qualifying, discount, discountLabel, merch, freeDelivery, giftEarned, earnedTier, tiers, giftLine, giftLines, giftChoice: choice };
    }
  };


  /* ---------- choose-one housewarming gift ----------
     A choice is remembered on this device. The theme adds exactly one private
     `_gift=true` line only after the merchandise subtotal reaches the threshold.
     Shopify's automatic BXGY discount is the checkout authority that makes the
     eligible line free; this code only manages the shopper's chosen cart line. */
  let giftSyncing = false;

  async function giftChange(key, quantity) {
    const res = await fetch("/cart/change.js", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: key, quantity })
    });
    if (!res.ok) throw new Error("gift cart change failed");
  }

  async function syncGift() {
    const threshold = (C().giftThreshold || 0) * 100;
    if (!threshold || !giftOptions().length || giftSyncing || !cartCache) return false;
    giftSyncing = true;
    let changed = false;
    try {
      let lines = cartCache.items.filter(isGiftLine);
      const giftTotal = lines.reduce((sum, line) => sum + line.line_price, 0);
      const qualifying = cartCache.total_price - giftTotal;
      const shouldHave = qualifying >= threshold && cartCache.items.some(item => !isGiftLine(item));
      const choice = chosenGift();
      const desiredId = shouldHave && choice ? String(choice.variantId) : "";
      let keptDesired = false;

      for (const line of lines) {
        const isDesired = desiredId && String(line.variant_id) === desiredId && !keptDesired;
        if (isDesired) {
          keptDesired = true;
          if (line.quantity !== 1) { await giftChange(line.key, 1); changed = true; }
        } else {
          await giftChange(line.key, 0);
          changed = true;
        }
      }

      if (desiredId && !keptDesired) {
        const res = await fetch("/cart/add.js", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: Number(desiredId), quantity: 1,
            properties: { _gift: "true", _gift_choice: desiredId }
          })
        });
        if (!res.ok) throw new Error("chosen gift unavailable");
        changed = true;
        OH.toast(choice.shortName + " added, on us");
      }

      if (changed) await fetchCart();
      return changed;
    } catch (e) {
      OH.toast("That gift could not be added. Please choose another.");
      if (cartCache) await fetchCart().catch(() => {});
      return false;
    } finally { giftSyncing = false; }
  }

  OH.chooseGift = async function (variantId) {
    const gift = giftById(variantId);
    if (!gift || gift.available === false) { OH.toast("That gift is not available right now."); return false; }
    OH.store.set("gift-choice", { variantId: String(gift.variantId) });
    document.dispatchEvent(new CustomEvent("oh:giftchoice", { detail: { gift } }));
    const changed = await syncGift();
    if (changed) document.dispatchEvent(new CustomEvent("oh:cart"));
    else {
      OH.renderDrawer && OH.renderDrawer();
      OH.paintGiftChoices && OH.paintGiftChoices(document);
    }
    return true;
  };
  OH.syncGift = syncGift;

  /* ---------- card support: swatch colors and placeholder posters ---------- */
  const SWATCHES = [
    [/oat|flax|linen|natural|barley|dune|sand|wheat|parchment/, "#DCCDAE"],
    [/cream|ivory|cloud|bone|chalk|snow|white/, "#F0EAD9"],
    [/oak(?!.*smoked)/, "#C69A6B"],
    [/walnut|espresso|chocolate|umber|brown/, "#5F4632"],
    [/charcoal|ink|graphite|black|onyx|midnight/, "#2E2A24"],
    [/smoked|smoke|tobacco/, "#4A423A"],
    [/grey|gray|pewter|slate|stone|heather/, "#8B857C"],
    [/sage|moss|fern|olive|green|eucalyptus/, "#71805F"],
    [/clay|rust|ochre|terracotta|cognac|tan|camel|caramel|sienna/, "#A2593B"],
    [/blue|indigo|navy|denim/, "#5D6B7A"],
    [/brass|gold|honey|amber/, "#B98A2F"],
    [/oxblood|wine|berry|red|brick/, "#71382B"],
    [/plum|mauve|lilac|aubergine/, "#6B4A5E"],
    [/putty|mushroom|taupe|greige/, "#A99B87"]
  ];
  OH.swatchColor = function (label) {
    const l = String(label).toLowerCase();
    for (const [re, c] of SWATCHES) if (re.test(l)) return c;
    return "#9C8B76";
  };

  const TYPE_SIL = [
    [/sectional/, "sectional"], [/sofa|couch|loveseat/, "sofa"], [/armchair|accent chair|lounge/, "armchair"],
    [/coffee table/, "coffeeTable"], [/side table|end table/, "sideTable"], [/console|credenza|media/, "console"],
    [/bookshelf|bookcase/, "bookshelf"], [/ottoman/, "ottoman"], [/bed(?!ding)/, "bed"], [/nightstand/, "nightstand"],
    [/dresser|chest/, "dresser"], [/bedding|duvet|sheet|quilt/, "bedding"], [/bench/, "bench"], [/wardrobe|armoire/, "wardrobe"],
    [/dining table/, "diningTable"], [/dining chair/, "diningChair"], [/sideboard/, "sideboard"], [/bar/, "barCabinet"],
    [/stool/, "stool"], [/desk/, "desk"], [/office chair/, "officeChair"], [/shelving|shelf/, "shelving"],
    [/pendant/, "pendant"], [/floor lamp/, "floorLamp"], [/table lamp|lamp/, "tableLamp"], [/sconce/, "sconce"],
    [/rug|runner/, "rug"], [/throw/, "throw"], [/cushion|pillow/, "cushion"], [/mirror/, "mirror"],
    [/vase/, "vase"], [/candle/, "candle"], [/art|print/, "wallArt"]
  ];
  OH.silForType = function (type) {
    const t = String(type || "").toLowerCase();
    for (const [re, s] of TYPE_SIL) if (re.test(t)) return s;
    return "object";
  };

  /* fill placeholder posters and swatch dots rendered by Liquid */
  OH.fillArt = function (root) {
    OH.qsa("[data-oh-poster]", root).forEach(el => {
      if (el.dataset.filled) return;
      el.dataset.filled = "1";
      const deptKey = el.getAttribute("data-dept") || "";
      const dept = (C().departments || []).find(d => deptKey.includes(d.key));
      el.innerHTML = OH.poster({
        tint: el.getAttribute("data-tint") || (dept ? dept.tint : "tungsten"),
        silhouette: el.getAttribute("data-sil") || OH.silForType(el.getAttribute("data-type")),
        id: el.getAttribute("data-id") || deptKey
      }, { lite: el.hasAttribute("data-lite"), wide: el.hasAttribute("data-wide") });
    });
    OH.qsa("[data-dept-poster]", root).forEach(el => {
      if (el.dataset.filled) return;
      el.dataset.filled = "1";
      el.innerHTML = OH.deptPoster(el.getAttribute("data-dept-poster"), { wide: el.hasAttribute("data-wide") });
    });
    OH.qsa("[data-swatch-label]", root).forEach(el => {
      el.style.background = OH.swatchColor(el.getAttribute("data-swatch-label"));
    });
  };

  /* ---------- flight into the bag ---------- */
  OH.motion = {
    reduced: false, /* Oak & Hour always keeps the full motion direction */
    revealObserver: null,
    observeReveals(root) {
      if (!OH.motion.revealObserver) {
        OH.motion.revealObserver = new IntersectionObserver(entries => {
          entries.forEach(en => {
            if (en.isIntersecting) { en.target.classList.add("on"); OH.motion.revealObserver.unobserve(en.target); }
          });
        }, { rootMargin: "0px 0px -8% 0px", threshold: 0.08 });
      }
      OH.qsa(".rv", root).forEach((el, i) => {
        if (!el.style.getPropertyValue("--d")) el.style.setProperty("--d", Math.min(i % 6, 5) * 70 + "ms");
        OH.motion.revealObserver.observe(el);
      });
    }
  };

  OH.flyToBag = function (srcEl) {
    if (!srcEl || !srcEl.animate) return;
    const bag = OH.qs("[data-open-cart]");
    if (!bag) return;
    const poster = srcEl.closest(".pdp-gallery") ? OH.qs(".poster", srcEl.closest(".pdp-gallery")) : srcEl;
    const r = poster.getBoundingClientRect(), b = bag.getBoundingClientRect();
    if (!r.width) return;
    const ghost = poster.cloneNode(true);
    Object.assign(ghost.style, {
      position: "fixed", left: r.left + "px", top: r.top + "px",
      width: r.width + "px", height: r.height + "px",
      zIndex: 130, pointerEvents: "none", borderRadius: "6px", overflow: "hidden", margin: 0
    });
    document.body.appendChild(ghost);
    ghost.animate([
      { transform: "none", opacity: 1 },
      { transform: `translate(${b.left + b.width / 2 - (r.left + r.width / 2)}px, ${b.top + b.height / 2 - (r.top + r.height / 2)}px) scale(0.06)`, opacity: 0.3 }
    ], { duration: 620, easing: "cubic-bezier(0.22,1,0.36,1)" }).onfinish = () => ghost.remove();
  };

  /* ---------- drawer ---------- */
  let drawerReturnFocus = null;
  OH.drawer = {
    async open() {
      drawerReturnFocus = document.activeElement;
      if (!cartCache) {
        try { await fetchCart(); }
        catch (e) { OH.toast("We could not open the bag. Check your connection and try again."); return; }
      }
      const drawer = OH.qs("[data-drawer]");
      drawer.removeAttribute("inert");
      drawer.setAttribute("aria-hidden", "false");
      drawer.classList.add("open");
      OH.qs("[data-scrim]").classList.add("open");
      document.body.classList.add("oh-overlay-open", "cart-open");
      renderDrawer();
      const closeBtn = OH.qs("[data-close-cart]");
      if (closeBtn) closeBtn.focus();
    },
    close() {
      const drawer = OH.qs("[data-drawer]");
      drawer.classList.remove("open");
      drawer.setAttribute("aria-hidden", "true");
      drawer.setAttribute("inert", "");
      OH.qs("[data-scrim]").classList.remove("open");
      document.body.classList.remove("oh-overlay-open", "cart-open");
      if (drawerReturnFocus && drawerReturnFocus.focus) drawerReturnFocus.focus();
      drawerReturnFocus = null;
    }
  };

  function giftOptionsHTML(compact) {
    const selected = chosenGift();
    return `<div class="gift-options${compact ? " is-compact" : ""}">
      ${giftOptions().map(gift => {
        const on = selected && String(selected.variantId) === String(gift.variantId);
        return `<button type="button" class="gift-option${on ? " selected" : ""}" data-gift-choice="${gift.variantId}" aria-pressed="${on}" ${gift.available === false ? "disabled" : ""}>
          <span class="gift-option-image"><img src="${OH.esc(gift.image || "")}" alt="" loading="lazy"></span>
          <span class="gift-option-copy"><b>${OH.esc(gift.shortName || gift.name)}</b><small>${gift.available === false ? "Unavailable" : OH.money(gift.valueCents) + " value"}</small></span>
          <span class="gift-option-check" aria-hidden="true">${on ? "✓" : ""}</span>
        </button>`;
      }).join("")}
    </div>`;
  }
  OH.giftOptionsHTML = giftOptionsHTML;

  OH.paintGiftChoices = function (root) {
    const selected = chosenGift();
    OH.qsa("[data-gift-choice]", root || document).forEach(button => {
      const on = !!selected && String(button.getAttribute("data-gift-choice")) === String(selected.variantId);
      button.classList.toggle("selected", on);
      button.setAttribute("aria-pressed", String(on));
      const check = button.querySelector(".gift-option-check");
      if (check) check.textContent = on ? "✓" : "";
    });
  };

  function giftChoiceHTML(t, compact) {
    const selected = chosenGift();
    const remaining = Math.max(0, C().giftThreshold * 100 - (t.qualifying || 0));
    const heading = t.giftEarned
      ? (selected ? "Your Complimentary Housewarming Gift" : "Choose Your Complimentary Gift")
      : "Choose Your Gift Early";
    const note = t.giftEarned
      ? (selected ? selected.shortName + " is in your bag and becomes free at checkout." : "Your order qualifies. Pick one before checkout.")
      : "Select your favorite now. Add " + OH.money(remaining) + " more to unlock it.";
    if (compact) {
      const status = t.giftEarned ? "Unlocked" : OH.money(remaining) + " away";
      return `<section class="gift-chooser gift-chooser-compact${t.giftEarned ? " is-qualified" : ""}" data-gift-chooser-ui>
        <button type="button" class="gift-chooser-toggle" data-gift-toggle aria-expanded="false">
          <span class="gift-chooser-thumb">${selected ? `<img src="${OH.esc(selected.image || "")}" alt="">` : `<span aria-hidden="true">+</span>`}</span>
          <span class="gift-chooser-summary"><small>Complimentary gift &middot; ${OH.esc(status)}</small><b>${OH.esc(selected ? selected.shortName : "Choose One of Three")}</b></span>
          <span class="gift-chooser-action">${selected ? "Change" : "Choose"}</span>
        </button>
        <div class="gift-chooser-panel" data-gift-panel hidden>
          <p>${OH.esc(note)}</p>
          ${giftOptionsHTML(true)}
        </div>
      </section>`;
    }
    return `<section class="gift-chooser${t.giftEarned ? " is-qualified" : ""}" data-gift-chooser-ui>
      <div class="gift-chooser-head"><span>Complimentary &middot; Choose One</span><b>${OH.esc(heading)}</b><p>${OH.esc(note)}</p></div>
      ${giftOptionsHTML(false)}
    </section>`;
  }
  OH.giftChoiceHTML = giftChoiceHTML;

  function progressHTML(t) {
    if (t.count === 0) return "";
    const dollars = (t.qualifying != null ? t.qualifying : t.subtotal) / 100;
    const gift = C().giftThreshold || 0;
    const tiers = t.tiers || [];
    let line, target;

    if (gift > 0 && dollars < gift) {
      line = `Add <b>${OH.money((gift - dollars) * 100)}</b> more to choose a <b>Complimentary Housewarming Gift</b>`;
      target = dollars / gift;
    } else {
      const next = tiers.find(x => dollars < x.spend);
      const got = t.earnedTier
        ? `<b>$${t.earnedTier.off}</b> off applied. `
        : (gift > 0 ? `<b>Your gift choice is unlocked.</b> ` : "");
      if (next) {
        line = `${got}Add <b>${OH.money((next.spend - dollars) * 100)}</b> more and take <b>$${next.off}</b> off`;
        target = dollars / next.spend;
      } else {
        line = `<b>$${t.earnedTier ? t.earnedTier.off : 0}</b> off, free delivery, and your chosen housewarming gift. That is the whole ladder`;
        target = 1;
      }
    }
    return `<div class="progress-line">${line}</div>
      <div class="progress-track"><div class="progress-fill" data-target="${Math.min(100, Math.round(target * 100))}"></div></div>`;
  }
  OH.progressHTML = progressHTML;

  let lastFillPct = 0;
  function animateFill(scope) {
    const fill = OH.qs(".progress-fill", scope);
    if (!fill) return;
    const target = parseFloat(fill.getAttribute("data-target")) || 0;
    fill.style.width = lastFillPct + "%";
    requestAnimationFrame(() => requestAnimationFrame(() => { fill.style.width = target + "%"; lastFillPct = target; }));
  }
  OH.animateFill = animateFill;

  function renderDrawer() {
    const body = OH.qs("[data-drawer-body]");
    const foot = OH.qs("[data-drawer-foot]");
    if (!body) return;
    const t = OH.cart.totals();

    if (t.count === 0) {
      const hl = OH.hourLine();
      body.innerHTML = `<div class="drawer-empty">
        <div class="eyebrow">${hl.t}</div>
        <h3>Your bag is empty</h3>
        <p style="color:var(--walnut); font-size:0.92rem">${hl.line}</p>
        <a class="btn btn-dark" href="/collections/bestsellers">See the bestsellers</a>
      </div>`;
      foot.innerHTML = "";
      return;
    }

    const merchandiseItems = t.cart.items.filter(item => !isGiftLine(item));
    body.innerHTML = merchandiseItems.map((it, idx) => {
      const img = it.image ? `<img src="${it.image.replace(/(\.[a-z]+)(\?|$)/, "_200x$1$2")}" alt="" loading="lazy">` : (window.OH.poster ? OH.poster({ tint: "tungsten", silhouette: "object", id: it.handle }, { lite: true }) : "");
      const opts = it.variant_title && it.variant_title !== "Default Title" ? it.variant_title : "";
      return `<div class="cart-line" style="--i:${idx}">
        <a class="cart-thumb" href="${it.url}" aria-label="View ${OH.esc(it.product_title)}">${img}</a>
        <div class="cart-info">
          <a class="cart-name" href="${it.url}">${OH.esc(it.product_title)}</a>
          ${opts ? `<div class="cart-variant">${OH.esc(opts)}</div>` : ""}
          <div class="cart-controls">
            <span class="qty">
              <button data-qty="-1" data-key="${it.key}" aria-label="Decrease">&minus;</button>
              <span>${it.quantity}</span>
              <button data-qty="1" data-key="${it.key}" aria-label="Increase">+</button>
            </span>
            <button class="cart-remove" data-remove="${it.key}">Remove</button>
          </div>
        </div>
        <div class="cart-price">${OH.money(it.final_line_price)}</div>
      </div>`;
    }).join("");

    const merchandiseCount = merchandiseItems.reduce((sum, item) => sum + item.quantity, 0);
    const welcome = OH.store.get("welcome", {});
    const codeRow = welcome.applied
      ? `<div class="code-note ok">Code ${C().welcomeCode} rides to checkout with you. Your best offer applies there.</div>`
      : `<details class="code-disclosure">
           <summary>Have a discount code?</summary>
           <form data-code-form>
             <input class="field" placeholder="Enter code" aria-label="Discount code">
             <button class="btn btn-ghost" type="submit">Apply</button>
           </form>
           <div class="code-note" data-code-note></div>
         </details>`;

    foot.innerHTML = `
      <div class="drawer-foot-scroll">
        ${progressHTML(t)}
        ${giftChoiceHTML(t, true)}
        ${codeRow}
        <div class="totals">
          <div class="totals-row"><span>Subtotal, ${merchandiseCount} ${merchandiseCount === 1 ? "piece" : "pieces"}</span><span>${OH.money(t.qualifying != null ? t.qualifying : t.subtotal)}</span></div>
          ${t.discount > 0 ? `<div class="totals-row saving"><span>${t.discountLabel}, applied at checkout</span><span>&minus;${OH.money(t.discount)}</span></div>` : ""}
          <div class="totals-row"><span>Delivery</span><span style="color:var(--tungsten-text); font-weight:600">FREE</span></div>
          <div class="totals-row grand"><span>Estimated total</span><span>${OH.money((t.qualifying != null ? t.qualifying : t.subtotal) - t.discount)}</span></div>
        </div>
        <div class="totals-note">Taxes are calculated at checkout. The best eligible offers apply automatically.</div>
      </div>
      <div class="drawer-checkout-stack">
        <a class="btn btn-primary btn-block" data-checkout href="/checkout">Continue to checkout</a>
        <a class="drawer-review-link" href="${C().routes.cart}">Review the full bag</a>
        <div class="drawer-assurance">Free delivery in 5 to 8 business days &middot; ${C().returnsDays}-day returns &middot; one-year guarantee</div>
      </div>`;
    animateFill(foot);
    OH.paintGiftChoices(foot);
  }
  OH.renderDrawer = renderDrawer;

  /* ---------- badges ---------- */
  function refreshBadges() {
    const cartCount = OH.cart.count();
    const c = OH.qs("[data-cart-count]");
    if (c) {
      c.textContent = cartCount;
      c.classList.toggle("show", cartCount > 0);
      const btn = c.closest("[data-open-cart]");
      if (btn) btn.setAttribute("aria-label", "Bag, " + cartCount + (cartCount === 1 ? " item" : " items"));
    }
  }

  /* ---------- viewport freeze ---------- */
  function freezeViewport() {
    document.documentElement.style.setProperty("--vhpx", window.innerHeight + "px");
  }
  let lastW = window.innerWidth;
  window.addEventListener("resize", () => {
    if (window.innerWidth !== lastW) { lastW = window.innerWidth; freezeViewport(); }
  });

  /* ---------- boot ---------- */
  document.addEventListener("DOMContentLoaded", () => {
    freezeViewport();
    refreshBadges();
    fetchCart().then(syncGift).then(() => { refreshBadges(); OH.renderDrawer && OH.renderDrawer(); }).catch(() => {});

    const header = OH.qs("[data-header]");
    if (header) window.addEventListener("scroll", () => header.classList.toggle("scrolled", window.scrollY > 8), { passive: true });

    const navToggle = OH.qs("[data-nav-toggle]");
    if (navToggle) {
      const nav = OH.qs("[data-nav]");
      const setNav = open => {
        nav.classList.toggle("open", open);
        navToggle.setAttribute("aria-expanded", String(open));
        navToggle.setAttribute("aria-label", open ? "Close menu" : "Menu");
        document.body.classList.toggle("nav-open", open);
        if (open) {
          const first = nav.querySelector("a, button");
          if (first) first.focus();
        }
      };
      navToggle.addEventListener("click", () => setNav(!nav.classList.contains("open")));
      const close = OH.qs("[data-nav-close]");
      if (close) close.addEventListener("click", () => { setNav(false); navToggle.focus(); });
      nav.addEventListener("click", e => { if (e.target.closest("a")) setNav(false); });
      document.addEventListener("keydown", e => {
        if (e.key === "Escape" && nav.classList.contains("open")) { setNav(false); navToggle.focus(); }
      });
    }

    document.addEventListener("click", e => {
      const giftToggle = e.target.closest("[data-gift-toggle]");
      if (giftToggle) {
        e.preventDefault();
        const chooser = giftToggle.closest("[data-gift-chooser-ui]");
        const panel = chooser && chooser.querySelector("[data-gift-panel]");
        if (panel) {
          const open = panel.hasAttribute("hidden");
          panel.toggleAttribute("hidden", !open);
          giftToggle.setAttribute("aria-expanded", String(open));
        }
        return;
      }
      const giftChoiceButton = e.target.closest("[data-gift-choice]");
      if (giftChoiceButton) {
        e.preventDefault();
        OH.chooseGift(giftChoiceButton.getAttribute("data-gift-choice"));
      }
      if (e.target.closest("[data-open-cart]")) { e.preventDefault(); OH.drawer.open(); }
      if (e.target.closest("[data-close-cart]")) OH.drawer.close();
      if (e.target.closest("[data-scrim]")) { OH.drawer.close(); document.dispatchEvent(new CustomEvent("oh:closeovers")); }
      const qty = e.target.closest("[data-qty]");
      if (qty) {
        const key = qty.getAttribute("data-key");
        const it = cartCache.items.find(i => i.key === key);
        if (it) OH.cart.change(key, it.quantity + parseInt(qty.getAttribute("data-qty"), 10));
      }
      const rem = e.target.closest("[data-remove]");
      if (rem) OH.cart.change(rem.getAttribute("data-remove"), 0);
      if (e.target.closest("[data-checkout]")) OH.track("begin_checkout", { ecommerce: { value: (cartCache ? cartCache.total_price : 0) / 100 } });
    });

    document.addEventListener("submit", e => {
      const codeForm = e.target.closest("[data-code-form]");
      if (codeForm) {
        e.preventDefault();
        const input = codeForm.querySelector("input");
        const val = (input.value || "").trim().toUpperCase();
        const note = codeForm.parentElement.querySelector("[data-code-note]");
        if (val === C().welcomeCode) {
          /* the discount URL primes the session so checkout has it waiting */
          fetch("/discount/" + encodeURIComponent(val)).finally(() => {
            const welcome = OH.store.get("welcome", {});
            welcome.applied = true;
            OH.store.set("welcome", welcome);
            document.dispatchEvent(new CustomEvent("oh:cart"));
          });
        } else if (note) {
          note.textContent = "That code does not look right. Codes arrive with the newsletter, in the footer.";
          input.select();
        }
      }
    });

    document.addEventListener("keydown", e => {
      if (e.key === "Escape") { OH.drawer.close(); document.dispatchEvent(new CustomEvent("oh:closeovers")); }
      if (e.key !== "Tab") return;
      const drawer = OH.qs("[data-drawer]");
      const overlay = OH.qs("[data-search-overlay]");
      const mobileNav = OH.qs("[data-nav]");
      const scope = drawer && drawer.classList.contains("open")
        ? drawer
        : (overlay && !overlay.hidden ? overlay : (mobileNav && mobileNav.classList.contains("open") ? mobileNav : null));
      if (!scope) return;
      const focusables = OH.qsa("a[href], button:not([disabled]), input, select, textarea", scope).filter(el => el.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0], last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    document.addEventListener("oh:cart", () => { refreshBadges(); renderDrawer(); });
    document.addEventListener("oh:giftchoice", () => OH.paintGiftChoices(document));
    OH.fillArt();
    OH.motion.observeReveals();
    document.dispatchEvent(new CustomEvent("oh:shell"));
  });
})();
